import { useEffect, useState } from "react";
import { RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { errMsg } from "../lib/api";

const ROWS = [
  ["transactions", "Betroffene Buchungen"],
  ["links_removed", "Beleg-Verknüpfungen, die entfernt werden"],
  ["receipts_deleted", "Belege, die gelöscht werden"],
  ["receipts_kept", "Belege behalten (auch außerhalb verknüpft)"],
  ["inbox_receipts_deleted", "Nicht zugeordnete Mail-Belege im Eingang, die gelöscht werden"],
  ["statuses_reset", "Status, die auf „Offen“ gesetzt werden"],
  ["jobs_cancelled", "Laufende Jobs, die abgebrochen werden"],
  ["agent_items_removed", "Agent-Erinnerungen / Nachrichten, die entfernt werden"],
];

export const ResetDialog = ({ open, onOpenChange, title, confirmWord, run, onDone, testPrefix }) => {
  const [plan, setPlan] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setPlan(null); setTyped("");
    run(true).then(setPlan).catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = async () => {
    setBusy(true);
    try {
      const r = await run(false, typed.trim());
      toast.success(r.noop ? "Nichts zu tun – bereits zurückgesetzt" : `${r.label} zurückgesetzt`);
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`${testPrefix}-reset-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Belege, Status, Mail-Treffer und Amazon-Hinweise werden zurückgesetzt. Buchungen, Notizen, KI-Erklärungen und das Auszug-PDF bleiben erhalten.</DialogDescription>
        </DialogHeader>
        {!plan ? <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
          <div className="grid gap-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid={`${testPrefix}-reset-counts`}>
            {ROWS.map(([k, label]) => (
              <div key={k} className="flex justify-between gap-4"><span className="text-slate-600">{label}</span>
                <b className="font-num text-slate-900" data-testid={`${testPrefix}-reset-count-${k}`}>{plan.counts[k]}</b></div>
            ))}
          </div>
        )}
        <label className="text-sm text-slate-700">Zur Bestätigung <b>{confirmWord}</b> eingeben:
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} className="mt-1" data-testid={`${testPrefix}-reset-confirm-input`} />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testPrefix}-reset-cancel-btn`}>Abbrechen</Button>
          <Button className="gap-1.5 bg-red-700 hover:bg-red-800" disabled={!plan || busy || typed.trim() !== String(confirmWord)} onClick={apply}
            data-testid={`${testPrefix}-reset-apply-btn`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Zurücksetzen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
